// 军衔工具函数 - 30级军衔体系
// 统一的军衔名称、阶段图标和颜色配置

import i18n from 'i18next';

/**
 * 获取军衔名称（支持多语言）
 * @param {number} level - 等级 (1-30+)
 * @returns {string} 军衔名称
 */
export const getMilitaryRankName = (level) => {
  const levelNames = {
    1: 'game_level_private', 2: 'game_level_private_first_class', 3: 'game_level_corporal',
    4: 'game_level_sergeant', 5: 'game_level_sergeant', 6: 'game_level_sergeant_major', 7: 'game_level_sergeant_major', 8: 'game_level_sergeant_major', 9: 'game_level_sergeant_major',
    10: 'game_level_lieutenant_junior', 11: 'game_level_lieutenant', 12: 'game_level_lieutenant', 13: 'game_level_lieutenant_senior',
    14: 'game_level_colonel_junior', 15: 'game_level_colonel', 16: 'game_level_colonel', 17: 'game_level_colonel_senior',
    18: 'game_level_general_junior', 19: 'game_level_general', 20: 'game_level_general', 21: 'game_level_general_senior',
    22: 'game_level_marshal', 23: 'game_level_marshal', 24: 'game_level_engineer', 25: 'game_level_academician',
    26: 'game_level_master', 27: 'game_level_architect', 28: 'game_level_legend', 29: 'game_level_master', 30: 'game_level_god'
  };
  const key = levelNames[level] || `rank_${level}`;
  return i18n.t(key);
};

/**
 * 获取军衔阶段信息（支持多语言）
 * @param {number} level - 等级 (1-30+)
 * @returns {object} 包含阶段名称、图标、颜色等信息
 */
export const getMilitaryRankStage = (level) => {
  const stageMap = {
    soldier: { stage: 'soldier', stageNameKey: 'stage_soldier', stageIcon: '🛡️', color: '#8B4513', levelRange: '1-3级', levelRangeKey: 'level_range_1_3' },
    sergeant: { stage: 'sergeant', stageNameKey: 'stage_sergeant', stageIcon: '⚔️', color: '#CD853F', levelRange: '4-9级', levelRangeKey: 'level_range_4_9' },
    lieutenant: { stage: 'lieutenant', stageNameKey: 'stage_lieutenant', stageIcon: '⭐', color: '#4169E1', levelRange: '10-13级', levelRangeKey: 'level_range_10_13' },
    colonel: { stage: 'colonel', stageNameKey: 'stage_colonel', stageIcon: '🌟', color: '#32CD32', levelRange: '14-17级', levelRangeKey: 'level_range_14_17' },
    general: { stage: 'general', stageNameKey: 'stage_general', stageIcon: '💎', color: '#FF6347', levelRange: '18-21级', levelRangeKey: 'level_range_18_21' },
    marshal: { stage: 'marshal', stageNameKey: 'stage_marshal', stageIcon: '👑', color: '#FFD700', levelRange: '22-25级', levelRangeKey: 'level_range_22_25' },
    legendary: { stage: 'legendary', stageNameKey: 'stage_legendary', stageIcon: '🌞', color: '#FF69B4', levelRange: '26-30级', levelRangeKey: 'level_range_26_30' },
    unknown: { stage: 'unknown', stageNameKey: 'stage_unknown', stageIcon: '🎯', color: '#74b9ff', levelRange: '30+级', levelRangeKey: 'level_range_30_plus' }
  };

  let stageKey, subLevelOffset;
  if (level >= 1 && level <= 3) {
    stageKey = 'soldier'; subLevelOffset = 0;
  } else if (level >= 4 && level <= 9) {
    stageKey = 'sergeant'; subLevelOffset = 3;
  } else if (level >= 10 && level <= 13) {
    stageKey = 'lieutenant'; subLevelOffset = 9;
  } else if (level >= 14 && level <= 17) {
    stageKey = 'colonel'; subLevelOffset = 13;
  } else if (level >= 18 && level <= 21) {
    stageKey = 'general'; subLevelOffset = 17;
  } else if (level >= 22 && level <= 25) {
    stageKey = 'marshal'; subLevelOffset = 21;
  } else if (level >= 26 && level <= 30) {
    stageKey = 'legendary'; subLevelOffset = 25;
  } else {
    stageKey = 'unknown'; subLevelOffset = 0;
  }

  const base = stageMap[stageKey];
  return {
    ...base,
    stageName: i18n.t(base.stageNameKey),
    levelRange: i18n.t(base.levelRangeKey),
    subLevel: level - subLevelOffset
  };
};

/**
 * 获取完整的军衔信息
 * @param {number} level - 等级 (1-30+)
 * @returns {object} 包含名称、阶段信息的完整军衔数据
 */
export const getMilitaryRankInfo = (level) => {
  const name = getMilitaryRankName(level);
  const stageInfo = getMilitaryRankStage(level);
  
  return {
    level,
    name,
    ...stageInfo
  };
};

/**
 * 获取等级显示文本（用于UI显示）
 * @param {number} level - 等级
 * @param {boolean} showIcon - 是否显示图标
 * @param {boolean} showLevel - 是否显示等级数字
 * @returns {string} 格式化的显示文本
 */
export const formatLevelDisplay = (level, showIcon = true, showLevel = true) => {
  const rankInfo = getMilitaryRankInfo(level);
  let display = '';
  
  if (showIcon) {
    display += `${rankInfo.stageIcon} `;
  }
  
  display += rankInfo.name;
  
  if (showLevel) {
    display += ` (等级 ${level})`;
  }
  
  return display;
};

/**
 * 根据等级获取CSS样式对象
 * @param {number} level - 等级
 * @returns {object} CSS样式对象
 */
export const getLevelStyle = (level) => {
  const stageInfo = getMilitaryRankStage(level);
  
  return {
    color: stageInfo.color,
    textShadow: `0 0 10px ${stageInfo.color}`,
    borderColor: stageInfo.color
  };
};